/** @jsxImportSource @emotion/react */

import { Button } from '@mui/material'
import { Form, Field } from 'react-final-form'
import { useState, useEffect } from 'react'
import OtpInput from 'react-otp-input'
import { useTheme } from '@emotion/react'
import Typography from '@mui/material/Typography'
import { MFAType, MFA_TYPES } from 'utils/services/auth/auth.service'
import { pluralize } from 'utils/pluralize'
import { BaseModal } from 'components/Modals/BaseModal'
import * as classes from './styles'

type MFAVerificationProps = {
  mfaType: MFAType
  onSubmit: (code: string) => Promise<void>
  onResend?: () => Promise<void>
}

const CODE_LENGTH = 6
const MISMATCH_ERROR_MESSAGE = 'Invalid code received for user'

const OTP_INPUT_CONTAINER_STYLE = {
  display: 'flex',
  gap: '12px',
  justifyContent: 'center',
}

type FormValues = {
  code: string
}

const validateCode = (value: string) => {
  if (!value) {
    return 'Verification code is required'
  }
  if (value.length < CODE_LENGTH) {
    return `Code must be at least ${CODE_LENGTH} characters`
  }
  if (value.length > CODE_LENGTH) {
    return `Code must be ${CODE_LENGTH} characters`
  }
  return undefined
}

export const MFAVerificationModal = ({ mfaType, onSubmit, onResend }: MFAVerificationProps) => {
  const [isResending, setIsResending] = useState(false)
  const [resendCountdown, setResendCountdown] = useState(0)
  const [submissionError, setSubmissionError] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)
  const theme = useTheme()

  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => {
        setResendCountdown(resendCountdown - 1)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [resendCountdown])

  const handleResend = async () => {
    if (!onResend || resendCountdown > 0) {
      return
    }

    setIsResending(true)
    try {
      await onResend()
      setResendCountdown(20)
      // Reset submission state to allow auto-submit with new code
      setHasAttemptedSubmit(false)
      setSubmissionError('')
    } catch (e) {
      console.error('Error resending SMS verification code:', e)
    } finally {
      setIsResending(false)
    }
  }

  const handleFormSubmit = async (values: FormValues) => {
    setIsLoading(true)
    setSubmissionError('')
    setHasAttemptedSubmit(true)
    try {
      await onSubmit(values.code)
    } catch (error) {
      console.error('Error submitting verification code:', error)

      if (error instanceof Error && error.message === MISMATCH_ERROR_MESSAGE) {
        const errorMessage =
          error.message === MISMATCH_ERROR_MESSAGE ? 'The code you entered is incorrect.' : error.message

        setSubmissionError(errorMessage)
        return
      }

      setSubmissionError('Invalid verification code. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const description =
    mfaType === MFA_TYPES.SMS
      ? 'Check your phone for the code and enter it below.'
      : 'Check your code in your Authenticator app.'

  const renderSecondaryButton = () => {
    if (mfaType === MFA_TYPES.TOTP) {
      return null
    }

    if (!onResend) {
      return null
    }

    return (
      <Button
        variant="contained"
        color="blue"
        onClick={handleResend}
        disabled={isResending || isLoading || resendCountdown > 0}
        css={classes.button}
        fullWidth
      >
        {isResending ? 'Sending...' : resendCountdown > 0 ? `Resend code (${resendCountdown}s)` : 'Resend code'}
      </Button>
    )
  }

  const renderResendCode = () => {
    if (mfaType !== MFA_TYPES.SMS) {
      return null
    }

    if (isResending) {
      return (
        <Typography variant="body3" css={classes.resendCodeText}>
          Resending code...
        </Typography>
      )
    }

    if (resendCountdown > 0) {
      return (
        <Typography variant="body3" css={classes.resendCodeText}>
          Request a new code in {resendCountdown} {pluralize(resendCountdown, 'second')}
        </Typography>
      )
    }

    return (
      <span onClick={handleResend} css={classes.textButton} role="button" tabIndex={0}>
        Resend the code
      </span>
    )
  }

  return (
    <BaseModal open css={classes.modal} onClose={() => undefined}>
      <Form<FormValues>
        onSubmit={handleFormSubmit}
        initialValues={{ code: '' }}
        render={({ handleSubmit, values, form }) => {
          const handleCodeChange = async (code: string) => {
            form.mutators?.setFieldTouched?.('code', true)
            // Clear submission error when user types
            if (submissionError) {
              setSubmissionError('')
            }
            // Set field value
            form.change('code', code)
            // Auto-submit when complete (only on first attempt or if no previous error)
            if (code.length === CODE_LENGTH && !hasAttemptedSubmit) {
              const errors = form.getState().errors
              if (!errors || Object.keys(errors).length === 0) {
                handleSubmit()
              }
            }
          }

          const isSubmitButtonDisabled = isResending || isLoading || values.code?.length !== CODE_LENGTH

          return (
            <div css={classes.modalContent}>
              <div css={classes.modalHeader}>
                <Typography css={classes.title} variant="body2">
                  Enter Verification Code
                </Typography>
                <Typography css={classes.description} variant="body2">
                  {description}
                </Typography>
              </div>

              <Field name="code" validate={validateCode}>
                {({ input, meta }) => {
                  const hasError = (meta.touched && !!meta.error) || !!submissionError

                  return (
                    <div css={classes.otpInputContainer}>
                      <OtpInput
                        value={input.value}
                        onChange={handleCodeChange}
                        numInputs={CODE_LENGTH}
                        inputType="number"
                        renderInput={(props) => (
                          <input
                            {...props}
                            disabled={isLoading}
                            css={classes.otpInputBase(theme, !!hasError, !!props.value)}
                          />
                        )}
                        containerStyle={OTP_INPUT_CONTAINER_STYLE}
                        shouldAutoFocus
                      />
                      {hasError && (
                        <Typography variant="caption" color="error" css={classes.otpErrorText}>
                          {submissionError || meta.error}
                        </Typography>
                      )}
                    </div>
                  )
                }}
              </Field>

              <div css={classes.buttonsContainer}>
                {renderSecondaryButton()}

                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={isSubmitButtonDisabled}
                  css={classes.button}
                  fullWidth
                >
                  Next
                </Button>
              </div>

              {mfaType === MFA_TYPES.SMS && <div css={classes.resendCodeContainer}>{renderResendCode()}</div>}
            </div>
          )
        }}
      />
    </BaseModal>
  )
}
